/**
 * Everything the Offers page does that is not rendering.
 *
 * The stats come from the server computed over ALL offers, never the filtered
 * list, and this module does not recompute them. That is the single rule most
 * easily lost on this page: computing them in the browser from the rows it
 * happens to be holding would make the portfolio figure move every time a
 * filter changed, which reads as data loss rather than as a bug.
 *
 * What this module DOES compute is which SECTION a row belongs to, which is a
 * different kind of claim: a partition of the rows the page is already
 * holding, where every row lands in exactly one place. `offerSections` is the
 * one definition of that partition, so no section can double-count a row or
 * drop one, and the page never re-asks "is this row used?" for itself.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";
import type { Tone } from "./tone";

/**
 * How a row got here.
 *
 * The union is the wire contract, not a guarantee about the runtime: the
 * column has allowed a non-manual value since the schema was written, and a
 * value this page has never heard of is a thing the server can start sending
 * without asking. Every reader below therefore takes a plain string and says
 * what it does with one it does not recognise.
 */
export type OfferSource = "manual" | "browser" | "email";

export interface OfferRow {
  id: number;
  /** Stable addressing fields; optional while an older API is still deployed. */
  card_id?: number;
  capture_key?: string | null;
  source: OfferSource;
  /** The day the issuer stopped listing it. Flagged for you, never deleted. */
  missing_since: string | null;
  /** The last capture that still found it listed. */
  last_seen_at: string | null;
  /** When the row was first written, which is what makes a row "new" here. */
  created_at: string | null;
  card_name: string;
  merchant: string;
  used: boolean;
  /** Read on the daily podcast as it nears expiry. */
  pinned?: boolean;
  /**
   * The server's verdict on whether the podcast feed carries this row: pinned,
   * enrolled, unused, dated and live. A pinned row can still be false.
   */
  read_on_podcast?: boolean;
  title: string;
  notes: string;
  reward: string;
  spend_threshold: number | null;
  /**
   * The server's legacy rendered label, carrying Streamlit markup
   * (":red[**4d left**]"). Declared because the server still sends it and
   * NEVER rendered: the toned `expiry` tuple below is what the page shows.
   */
  expiry_label: string;
  enrolled: boolean;
  /** Spent toward `spend_threshold`, as last read off the issuer's tracker. */
  spend_progress: number | null;
  /** YYYY-MM-DD that figure was read. */
  progress_as_of: string | null;
  /** The issuer's own how-to-use text, terms and cap; null until captured. */
  details?: string | null;
  fine_print?: string | null;
  max_reward?: number | null;
  action: "mark_used" | "undo";
  /** [tone, text] under the server's thresholds. Never markup. */
  expiry: [Tone, string];
  expired: boolean;
  /** Inside the window the headline counts as ending soon. */
  expires_soon: boolean;
}

export interface OfferStats {
  active_count: number;
  /** Enrolled offers only: what is actually claimable. */
  total_potential: number;
  expiring_soon_count: number;
  expiring_soon_total: number;
  /**
   * Rows the issuer has stopped listing, as the server counts them.
   *
   * Declared and deliberately not rendered. The page partitions those rows
   * itself (a used or expired one leaves the "No longer listed" section), so a
   * server count printed over that section would sit above a different number
   * of rows and read as a bug in whichever of the two is wrong. The section
   * counts itself instead.
   */
  missing_count: number;
}

export interface CardOption {
  id: number;
  label: string;
}

export interface OffersDto {
  today: string;
  card_options: string[];
  status_options: string[];
  cashback_types: string[];
  cards: CardOption[];
  offers: OfferRow[];
  stats: OfferStats;
  /** The "ends soon" window, in days. */
  soon_days: number;
  /** When the most recent capture ran. Null before the first one. */
  last_capture_at: string | null;
}

export interface OffersFilters {
  card?: string;
  status?: string;
}

export interface NewOfferInput {
  card_id: number;
  merchant: string;
  cashback_type: string;
  cashback_amount?: number;
  spend_threshold?: number;
  expires_on?: string;
  title?: string;
  notes?: string;
  enrolled?: boolean;
}

export function offersQuery(f: OffersFilters): string {
  const q = new URLSearchParams();
  if (f.card) q.set("card", f.card);
  if (f.status) q.set("status", f.status);
  const s = q.toString();
  return s ? `?${s}` : "";
}

export async function fetchOffers(f: OffersFilters = {}): Promise<OffersDto> {
  const res = await authedFetch(`/api/offers${offersQuery(f)}`);
  return (await orThrow(res, "Could not load offers")) as OffersDto;
}

export async function createOffer(input: NewOfferInput): Promise<number> {
  const res = await authedFetch("/api/offers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await orThrow(res, "Could not add the offer")) as { id: number };
  return body.id;
}

/**
 * Mark used, or undo it.
 *
 * `used_on` is omitted when marking rather than defaulted in the browser: the
 * server stamps its own date, and a client clock that is wrong or in another
 * timezone would otherwise record a redemption on the wrong day.
 */
export async function setOfferUsed(
  offerId: number,
  used: boolean,
): Promise<void> {
  const res = await authedFetch(`/api/offers/${offerId}/used`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ used }),
  });
  await orThrow(res, "Could not update the offer");
}

export async function setOfferPinned(
  offerId: number,
  pinned: boolean,
): Promise<void> {
  const res = await authedFetch(`/api/offers/${offerId}/pinned`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pinned }),
  });
  await orThrow(res, "Could not update the offer");
}

export async function deleteOffer(offerId: number): Promise<void> {
  const res = await authedFetch(`/api/offers/${offerId}`, { method: "DELETE" });
  await orThrow(res, "Could not delete the offer");
}

/**
 * The label on the used/undo button.
 *
 * Driven by the server's `action` verdict, not by re-reading `used`. The two
 * should always agree, and deriving it here would create a second definition
 * that could disagree after a partial refresh.
 */
export function actionLabel(action: OfferRow["action"]): string {
  return action === "undo" ? "Undo" : "Mark used";
}

/**
 * How far a spend requirement has got, or null when there is nothing to draw:
 * no requirement (a zero threshold means none, as for "min spend") or no
 * reading yet. Overspend clamps to full rather than reporting a negative
 * remainder.
 */
export function spendProgress(
  o: Pick<OfferRow, "spend_threshold" | "spend_progress">,
): { fraction: number; remaining: number } | null {
  if (!o.spend_threshold || o.spend_progress == null) return null;
  return {
    fraction: Math.min(1, Math.max(0, o.spend_progress / o.spend_threshold)),
    remaining: Math.max(0, o.spend_threshold - o.spend_progress),
  };
}

// -- dates --------------------------------------------------------------------

const MONTHS = "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" ");

/**
 * The calendar day written in a server timestamp, or null if there isn't one.
 *
 * A PREFIX match, and that is load-bearing rather than lazy. `created_at` has
 * two writers and so two shapes: a captured row carries a bare `YYYY-MM-DD`,
 * the run's own Pacific day, while a hand-typed one carries the column's
 * `datetime('now')` default, a UTC instant. Taking the leading day reads both
 * without either being parsed as the other. Nothing here may parse `created_at`
 * as an instant or do arithmetic on it: an evening capture's Pacific day is
 * already the next day in UTC, which is how a row falls out of its own run.
 */
function dayParts(value: string | null): [number, number, number] | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // The CALENDAR, not just the digit shape. `Date.UTC` rolls 2026-02-31 silently
  // to March 3, so a shape-only check hands every caller downstream a date that
  // is wrong rather than one it can refuse: "Gone Feb 31" rendered as if real,
  // and a day count colliding with a different day's. Round-tripping is what
  // tells an impossible day from a real one. Same fix as `awardwallet.parse_day`,
  // whose regex fallback returned "2026-02-30" verbatim and took down a page.
  const at = new Date(Date.UTC(y, mo - 1, d));
  if (
    at.getUTCFullYear() !== y ||
    at.getUTCMonth() !== mo - 1 ||
    at.getUTCDate() !== d
  ) {
    return null;
  }
  return [y, mo, d];
}

function isoDay(value: string | null): string | null {
  const p = dayParts(value);
  return p ? `${p[0]}-${String(p[1]).padStart(2, "0")}-${String(p[2]).padStart(2, "0")}` : null;
}

/**
 * "Sep 28", from a server timestamp, or "" when there is no day in it.
 *
 * Read off the string's own digits rather than through `new Date`, which
 * parses a bare `2026-09-28` as UTC midnight and so renders the day before in
 * Pacific. Not `flightsPage.shortDate`: that one is typed for the watch-list
 * contract's `Value` union and lives in the Flights module, and Offers
 * depending on Flights for a date would outlive whichever page is deleted
 * first.
 */
export function shortDay(value: string | null): string {
  const p = dayParts(value);
  return p ? `${MONTHS[p[1] - 1]} ${p[2]}` : "";
}

/** Whole days from one server day to another, or null if either is unreadable. */
export function daysBetween(from: string | null, to: string | null): number | null {
  const a = dayNumber(from);
  const b = dayNumber(to);
  return a === null || b === null ? null : Math.round(b - a);
}

/**
 * A server timestamp's day as a day count, for arithmetic that never touches a
 * zone. Shares `dayParts` with every other reader, so an impossible day is
 * refused here too rather than rolling over into a real one.
 */
function dayNumber(value: string | null): number | null {
  const p = dayParts(value);
  return p ? Date.UTC(p[0], p[1] - 1, p[2]) / 86_400_000 : null;
}

/**
 * Was `value`'s day within `days` of `today`? A future day counts as within.
 *
 * `today` is the server's Pacific day and so agrees with a captured row's own.
 * A hand-typed row's UTC instant can read a day AHEAD of it, which is why a
 * future day counts: against a week-long window one day is noise, and it errs
 * toward calling a row new, which is the direction that shows you something
 * rather than hiding it.
 */
function withinDays(value: string | null, today: string, days: number): boolean {
  const at = dayNumber(value);
  const now = dayNumber(today);
  return at != null && now != null && at >= now - days;
}

/**
 * True for a `browser` row whose calendar day is `last_capture_at`'s day.
 *
 * Both halves are required. The source, because the section says what the
 * capture brought in, and `browser` is what `last_capture_at` measures. The
 * day rather than "at or after", because the marker is the newest capture
 * there has been, so anything later is by definition not one.
 *
 * Both sides reduce to a day rather than comparing as instants: `created_at`
 * may be a bare date where the marker carries an instant, and `Date.parse`
 * reads a bare date as UTC midnight, which sorts before a capture that ran
 * that afternoon. False when either is missing or unreadable, so the group
 * stays empty rather than catching everything.
 */
export function fromLastCapture(
  o: Pick<OfferRow, "source" | "created_at">,
  lastCaptureAt: string | null,
): boolean {
  if (o.source !== "browser") return false;
  const day = isoDay(lastCaptureAt);
  return day != null && isoDay(o.created_at) === day;
}

// -- provenance ---------------------------------------------------------------

/** One entry per `source` the page can name: the row badge. */
const SOURCES: Record<string, string> = {
  browser: "Captured",
  manual: "Typed",
  email: "Emailed",
};

/**
 * The provenance badge, or null for a `source` this page cannot name.
 *
 * Null rather than the raw enum value, because the badge is a claim about
 * whether the row maintains itself, and a value nobody has classified supports
 * no such claim. `automatic` is that claim: a captured or emailed row is
 * rewritten by its channel on every run, a typed one is never overwritten.
 */
export function sourceBadge(
  source: string,
): { label: string; automatic: boolean } | null {
  const label = SOURCES[source];
  if (!label) return null;
  return { label, automatic: source !== "manual" };
}

// -- sections -----------------------------------------------------------------

/**
 * How many live rows the server's `active_count` and `total_potential` leave
 * out because they are not enrolled. Both figures count live, enrolled rows
 * only, so the summary names this number rather than letting such a row sit
 * on the page contributing to nothing above it.
 */
export function uncountedCount(offers: OfferRow[]): number {
  return offers.filter((o) => !o.used && !o.expired && !o.enrolled).length;
}

/**
 * The summary's "captured Oct 14", or "" before any capture has run.
 *
 * The marker itself: the server derives it from the newest `last_seen_at`
 * among captured rows, so it is null exactly when no captured row exists.
 */
export function capturedSummary(
  dto: Pick<OffersDto, "today" | "last_capture_at">,
): string {
  const day = isoDay(dto.last_capture_at);
  if (day == null) return "";
  return day === dto.today ? "captured today" : `captured ${shortDay(day)}`;
}

export type AttentionKey = "soon" | "new" | "added";

export const ATTENTION_TITLES: Record<AttentionKey, string> = {
  soon: "Ending soon",
  new: "New since you last looked",
  added: "Added during the last capture",
};

export interface AttentionGroup {
  key: AttentionKey;
  title: string;
  offers: OfferRow[];
}

export interface OfferSections {
  /** Live and pinned: read on the podcast as it nears expiry. Shown first. */
  pinned: OfferRow[];
  /** Only the groups that have rows, in the order the page shows them. */
  attention: AttentionGroup[];
  /** Offers, not rows: one on four cards counts once. */
  attentionCount: number;
  /** Live, and the issuer has stopped listing it. */
  gone: OfferRow[];
  /** Live, and asking nothing of you. Shown grouped by the cards it is on. */
  rest: OfferRow[];
  used: OfferRow[];
  expired: OfferRow[];
}

/**
 * How long a row keeps reading as new.
 *
 * A fixed window because nothing records when you last looked: there is no
 * such marker in the payload, and putting one in browser storage would make
 * the section's contents depend on which device opened the page. When the
 * server does publish a marker, this becomes exact and the constant goes.
 */
export const NEW_WINDOW_DAYS = 7;

/**
 * Every offer, sorted into the page's sections. One row, one section, and
 * one live offer's copies on different cards all in the same one.
 *
 * The order of the tests below is PRECEDENCE, not display order: the groups
 * come back in the mockup's order (soon, new, added) while `added` is decided
 * before `new`, because "the last capture wrote this" is the more specific
 * fact about a row that satisfies both. `used` and `expired` come first
 * because they are settled -- a used offer is not also ending soon -- and
 * `missing_since` comes before `pinned` because "the issuer dropped it" is a
 * question only you can answer. `pinned` comes before the attention groups:
 * a pinned row is one you already chose to follow, so it leaves them rather
 * than showing twice.
 */
export function offerSections(
  dto: Pick<OffersDto, "offers" | "today" | "last_capture_at">,
  newWindowDays = NEW_WINDOW_DAYS,
): OfferSections {
  const used: OfferRow[] = [];
  const expired: OfferRow[] = [];
  const gone: OfferRow[] = [];
  const live: OfferRow[] = [];
  const pinned: OfferRow[] = [];
  const soon: OfferRow[] = [];
  const fresh: OfferRow[] = [];
  const added: OfferRow[] = [];
  const rest: OfferRow[] = [];

  // Settled and dropped are facts about one card's copy, so they are decided
  // row by row.
  for (const o of dto.offers) {
    if (o.used) used.push(o);
    else if (o.expired) expired.push(o);
    else if (o.missing_since) gone.push(o);
    else live.push(o);
  }
  // A live offer is decided once for every card it is on, so the same offer
  // never sits in two sections: any card's copy that would claim it for an
  // earlier section claims the rest along with it.
  for (const g of groupByOffer(live)) {
    const any = (test: (o: OfferRow) => boolean) => g.offers.some(test);
    const into = any((o) => Boolean(o.pinned)) ? pinned
      : any((o) => o.expires_soon) ? soon
        : any((o) => fromLastCapture(o, dto.last_capture_at)) ? added
          : any((o) => withinDays(o.created_at, dto.today, newWindowDays)) ? fresh
            : rest;
    into.push(...g.offers);
  }

  const groups: [AttentionKey, OfferRow[]][] = [
    ["soon", soon],
    ["new", fresh],
    ["added", added],
  ];
  return {
    pinned,
    attention: groups
      .filter(([, offers]) => offers.length > 0)
      .map(([key, offers]) => ({ key, title: ATTENTION_TITLES[key], offers })),
    attentionCount: groupByOffer([...soon, ...fresh, ...added]).length,
    gone,
    rest,
    used,
    expired,
  };
}

/** One offer as listed on one or more cards: each card's own row, in order. */
export interface OfferGroup {
  /** The first row's id, stable for as long as that row exists. */
  key: string;
  offers: OfferRow[];
}

/**
 * What makes two rows the same offer on different cards. An issuer lists one
 * offer on every card that carries it (Chase adds it to all of them), so
 * without this the page shows it once per card.
 */
function sameOffer(o: OfferRow): string {
  return [o.merchant, o.title, o.reward, o.expiry[1]].join("\u0000");
}

/**
 * `rows` with the same offer on different cards folded into one group, in
 * the order each group's first row appears. A card that lists an offer twice
 * keeps two groups: those are two rows of its own, not one offer on two cards.
 */
export function groupByOffer(rows: OfferRow[]): OfferGroup[] {
  const groups: OfferGroup[] = [];
  const byKey = new Map<string, OfferGroup[]>();
  for (const o of rows) {
    const k = sameOffer(o);
    const candidates = byKey.get(k) ?? [];
    let g = candidates.find((c) => !c.offers.some((x) => x.card_name === o.card_name));
    if (!g) {
      g = { key: String(o.id), offers: [] };
      candidates.push(g);
      byKey.set(k, candidates);
      groups.push(g);
    }
    g.offers.push(o);
  }
  return groups;
}

/**
 * `groups` split by the cards they are on, card sets in the order their first
 * group appears, groups in the order given (the server's urgency order). An
 * offer on four cards is listed once, under all four, rather than once under
 * each.
 */
export function byCards(groups: OfferGroup[]): { cards: string; groups: OfferGroup[] }[] {
  const sets = new Map<string, OfferGroup[]>();
  for (const g of groups) {
    const cards = g.offers.map((o) => o.card_name).join(" · ");
    const list = sets.get(cards);
    if (list) list.push(g);
    else sets.set(cards, [g]);
  }
  return [...sets].map(([cards, groups]) => ({ cards, groups }));
}

/** How far out the live page's "Expires on" box starts. */
export const DEFAULT_EXPIRY_DAYS = 60;

/**
 * The date the "Expires on" field starts on, given the server's today.
 *
 * The field starts at today + 60 days so it cannot be submitted blank: a NULL
 * `expires_on` is treated everywhere as "never expires" (excluded from
 * `expiring_soon`, never counted as expired), and adding an offer without
 * touching the date is the ordinary gesture.
 *
 * Built from local calendar parts, never by slicing a UTC ISO string: near
 * midnight `toISOString().slice(0, 10)` returns tomorrow.
 */
export function defaultExpiry(today: string, days = DEFAULT_EXPIRY_DAYS): string {
  const [y, m, d] = today.split("-").map(Number);
  if (!y || !m || !d) return "";
  const at = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}
